/* eslint-disable react/forbid-prop-types */
import React, { useState, useEffect, useRef, useImperativeHandle, useCallback } from 'react';
import { ArrowIcon as IconDown } from '../../Icons';
import { Loading } from '../../Loading';
import { Button } from '../../Button';
import { isNullOrEmptyArray } from '../../../util/arrays';
import { removerCaracteresEspeciais } from '../../../util/formatters/stringNormalizer';
import { defaultSelectedItensFormatter } from './formatter';
import Option from './Option';
import * as Ui from './styles';
import InputSearch from '../../Search/InputSearch';

function Selector({
  /**
   * Marcador que indica se o seletor esta desabilidado, como input html
   */
  disabled = false,
  /**
   * uma opção de estilo para colocar sobre fundo branco
   */
  dark = false,
  /**
   * caso desejem utilizar o componente diretamente num form (opcional)
   */
  inputName = '',
  /**
   * tipo do seletor 'single' | 'multi' (default: 'single')
   */
  type = 'single',
  /**
   * texto a ser exibido no seletor
   */
  placeholder = 'Pesquisa',
  /**
   * texto a ser exibido no seletor
   */
  inputSearchPlaceholder = 'Digite o texto da busca',
  /**
   * callback quando o estado do seletor muda
   */
  onChange = null,
  /**
   * callback para quando a ação de remover um item é acionada
   */
  onTrashItem = null,
  /**
   * callback para quando a ação de editar um item é acionada
   */
  onEditItem = null,
  /**
   * array de dados a serem exibidos '[{ value : string, label : string }]'
   */
  data = [],
  /**
   * array com os indices que ja iniciam selecionados [1, 2...]
   */
  initialSelectedIndexes = [],
  /**
   * funcao opcional que customiza a exibicao dos itens selecionados
   */
  selectedItensFormatter = defaultSelectedItensFormatter,
  /**
   * indicador se deve-se exibir um loading no componente
   */
  isLoading = false,
  /**
   * caso deseje adicionar a função de remover itens do selector
   */
  withRemovableItem = false,
  /**
   * caso deseja adicionar a função de editar itens do selector
   */
  withEditableItem = false,
  /**
   * indicador se a filtragem interna deve ser ativada ou nao
   */
  enableFilter = false,
  /**
   * TODO: ver doc
   */
  optionWithIcon = true,
  /**
   * estilo
   */
  className,
  /**
   * tema
   */
  theme,
}, ref) {
  // ref
  const selectorRef = useRef(null);
  const inputSearchRef = useRef(null);
  // state
  const [itensSelecionados, setItensSelecionados] = useState([/* {index, label, value} */]);
  const [dataSet, setDataSet] = useState([/* {index, label, value} */]);
  const [dataSetToShow, setDataSetToShow] = useState([/* {index, label, value} */]);
  const [exibir, setExibir] = useState(false);
  // callbacks
  const initialize = useCallback(() => {
    const newDataSet = data.map((item, index) => {
      const newData = { ...item, index };
      return newData;
    });
    setItensSelecionados([]);
    setDataSet(newDataSet);
    setDataSetToShow(newDataSet);
    if (inputSearchRef.current) {
      inputSearchRef.current.clear();
    }
  }, [data, inputSearchRef]);

  /**
   * Recupera o tipo de input a ser inserido no componente
   */
  function getOptionType() {
    const optionType = {
      single: 'radio',
      multi: 'checkbox',
    };
    return optionType[type];
  }

  /**
   * Verifica se um indice deve ou nao estar selecionado
   */
  function isChecked(index) {
    const result = itensSelecionados.some((item) => item.index === index);
    return result;
  }

  /**
   *
   */
  function handleSelectedItems(newItensSelecionados) {
    // atualiza o estado
    setItensSelecionados(newItensSelecionados);
    // propaga a alteração
    if (onChange) {
      const sel = newItensSelecionados.map((item) => ({
        label: item.label, value: item.value,
      }));
      onChange(sel); // retorna no formato de entrada
    }
  }

  /**
   * Handler para evento de selecao unica, efeito de radio
   */
  function singleHandleNewCheck(dataItem) {
    if (exibir) setExibir(!exibir);
    const newItensSelecionados = [dataSet[dataItem.index]];
    handleSelectedItems(newItensSelecionados);
  }

  /**
   * Handler para evento de remocao em selecao unica,
   * basicamente nao faz nada visto que trata-se do
   * efeito de radio
   */
  // eslint-disable-next-line no-unused-vars
  function singleHandleNewRemotion(dataItem) {
    // nada a se fazer
  }

  /**
   * Handler para evento de nova selecao em selecao multipla
   */
  function multiHandleNewCheck(dataItem) {
    const newItensSelecionados = [...itensSelecionados];
    newItensSelecionados.push(dataSet[dataItem.index]);
    handleSelectedItems(newItensSelecionados);
  }

  /**
   * Handler para evento de desmarcacao em selecao multipla
   */
  function multiHandleNewRemotion(dataItem) {
    const newItensSelecionados = itensSelecionados.filter(
      (item) => item.index !== dataItem.index,
    );
    handleSelectedItems(newItensSelecionados);
  }

  /**
   * Handler para clicks fora do componente
   */
  function handleClickOutside(event) {
    if (selectorRef.current && !selectorRef.current.contains(event.target)) {
      setExibir(false);
    }
  }

  /**
   * Funcao para filtrar os itens do dataSet
   */
  function handleChangeText(searchText) {
    const filterText = searchText && removerCaracteresEspeciais(searchText.toLowerCase());
    const filterResult = dataSet.filter((item) => removerCaracteresEspeciais(item.label)
      .toLowerCase()
      .includes(filterText));
    setDataSetToShow(filterResult);
  }

  // handleMap to callback
  const handleMap = {
    multi: {
      handleNewCheck: multiHandleNewCheck,
      handleNewRemotion: multiHandleNewRemotion,
    },
    single: {
      handleNewCheck: singleHandleNewCheck,
      handleNewRemotion: singleHandleNewRemotion,
    },
  };

  /**
   * Callback acionado quando um evento de selecao e desmarcacao ocorre,
   * atualiza os itens selecionados com a estrategia correta e notifica
   * a camada superior sobre a alteracao
   *
   * @param {*} data dados sobre o evento { inputName: string, label:string, value:string, index:number, isChecked:boolean }
   */
  function handleChange(dataItem) {
    if (dataItem.isChecked) {
      handleMap[type].handleNewCheck(dataItem);
    } else {
      handleMap[type].handleNewRemotion(dataItem);
    }
  }

  function handleTrash(dataItem) {
    if (onTrashItem) {
      const { label, value } = dataItem;
      const trashedItem = { label, value };
      onTrashItem(trashedItem);
    }
  }

  function handleEdit(dataItem) {
    if (onEditItem) {
      const { label, value } = dataItem;
      const trashedItem = { label, value };
      onEditItem(trashedItem);
    }
  }

  /**
   * api
   */
  useImperativeHandle(ref, () => ({
    selectItemIndex: (index) => {
      if (!isNullOrEmptyArray(dataSet)) {
        const dataItem = { ...dataSet[index], isChecked: true };
        handleChange(dataItem);
      }
    },
    selectItemValue: (value) => {
      if (!isNullOrEmptyArray(dataSet)) {
        const filteredDataSet = dataSet.filter((ds) => ds.value === value);
        if (filteredDataSet.length > 0) {
          const dataItem = { ...filteredDataSet[0], isChecked: true };
          handleChange(dataItem);
        }
      }
    },
    getSelectedItems() {
      return itensSelecionados;
    },
    refresh: () => {
      initialize();
    },
  }));

  /**
   * Garantindo que durante o carregamento, nenhum item sera exibido
   */
  useEffect(() => {
    if (isLoading) {
      setDataSet([]);
      setDataSetToShow([]);
      if (inputSearchRef.current) {
        inputSearchRef.current.clear();
      }
    }
  }, [isLoading]);

  /**
   * Registrando listeners para fechar automaticamente o
   * seletor caso ocorra click fora do componente
   */
  useEffect(() => {
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [selectorRef]);

  /**
   * Adicionar index para data set
   * e inicializa dataSetToShow
   */
  useEffect(() => {
    initialize();
  }, [initialize]);

  /**
   * Atualizando o dataSetToShow que sera exibido quando [dataSet]
   */
  useEffect(() => {
    // selected itens
    if (!isNullOrEmptyArray(initialSelectedIndexes)) {
      const sl = dataSet.filter((dataItem) => initialSelectedIndexes.includes(dataItem.index));
      setItensSelecionados(sl);
    }
  }, [dataSet, initialSelectedIndexes]);

  // render selector body
  function renderBody(innerPlaceholder) {
    return (
      <div style={{ background: 'inherit' }}>
        <div className={dark ? 'selector-search-wrapper dark' : 'selector-search-wrapper'}>
          {enableFilter && (
            <InputSearch
              ref={inputSearchRef}
              placeholder={innerPlaceholder}
              onChangeText={handleChangeText}
            />
          )}
        </div>
        <div className="selector-data-wrapper">
          {dataSetToShow.map((dataItem) => (
            <Option
              dark={dark}
              isRemovable={withRemovableItem}
              isEditalble={withEditableItem}
              withIcon={optionWithIcon}
              key={`${dataItem.index}_${dataItem.label}`}
              inputName={inputName}
              index={dataItem.index}
              label={dataItem.label}
              value={dataItem.value}
              type={getOptionType()}
              isChecked={isChecked(dataItem.index) && optionWithIcon}
              onChange={handleChange}
              onTrash={handleTrash}
              onEdit={handleEdit}
              theme={theme}
            />
          ))}
        </div>
      </div>
    );
  }

  function renderLoadingSpinner() {
    return <Loading size={30} running={isLoading} />;
  }

  return (
    <Ui.Container
      className={`${className || ''} ${dark ? 'seletor-wrapper dark' : 'seletor-wrapper'}`}
      ref={selectorRef}
      exibir={exibir}
      title={placeholder}
    >
      {/* selector button */}
      <button
        type="button"
        className="seletor-head"
        onClick={() => !disabled && setExibir(!exibir)}
      >
        <span className={isNullOrEmptyArray(itensSelecionados) ? 'title' : 'title selected'}>
          {isNullOrEmptyArray(itensSelecionados)
            ? placeholder
            : selectedItensFormatter(itensSelecionados, type)}
        </span>
        <IconDown
          size={20}
          color="#610082"
          className={`icondown ${exibir ? 'active' : 'noActive'}`}
        />
      </button>
      {/* dropdown list */}
      <div className="seletor-body">
        <div className="body-closer-wrapper">
          <h3 className="selector-title">{placeholder}</h3>
          <Button
            clear
            title="Fechar"
            className="body-closer"
            onClick={() => setExibir(false)}
          >
            &times;
          </Button>
        </div>
        {isLoading ? renderLoadingSpinner() : renderBody(inputSearchPlaceholder)}
      </div>
    </Ui.Container>
  );
}

export default React.forwardRef(Selector);
